import type { FeldWert, KonfigurationsFeld } from "@repo/shared";

import { euro } from "../lib/regeln";
import { AuswahlKarten } from "./konfigurator/AuswahlKarten";
import { AuswahlTabelle } from "./konfigurator/AuswahlTabelle";
import { FeldAktionen } from "./konfigurator/FeldAktionen";
import { Beschriftung, FeldFuss } from "./konfigurator/FeldRahmen";
import { Knopfreihe } from "./konfigurator/Knopfreihe";
import { BildAuswahl } from "./BildAuswahl";

type MassFeld = Extract<KonfigurationsFeld, { art: "mass" }>;

/** Höchstens so viele Stufen (Knöpfe) in einer Knopfreihe – darüber bleibt das Zahlenfeld. */
const MAX_KNOPF_STUFEN = 12;

/** Wertebereich eines Maßes – ohne Herstellergrenzen nur die Einheit. */
function bereich(feld: MassFeld): string {
  const einheit = feld.einheit ? ` ${feld.einheit}` : "";
  // 0 bis 100.000 setzt der erfal-Adapter, wenn erfal keine Grenzen nennt.
  if (feld.min <= 0 && feld.max >= 100_000) return feld.einheit ? ` (${feld.einheit})` : "";
  return ` (${feld.min}–${feld.max}${einheit})`;
}

/** Aufpreis eines Schalters: Anzeigepreis je Einheit, sonst der fixe Aufpreis. */
function aufpreis(feld: Extract<KonfigurationsFeld, { art: "schalter" }>): string | undefined {
  if (feld.preis) return ` (+${euro(feld.preis.nettoCents)}${feld.preis.einheit ? ` je ${feld.preis.einheit}` : ""})`;
  return feld.aufpreisCents ? ` (+${euro(feld.aufpreisCents)})` : undefined;
}

type FeldEingabeProps = {
  feld: KonfigurationsFeld;
  wert: FeldWert | undefined;
  onChange: (wert: FeldWert) => void;
  /** Nach der Wahl einer Karte – der Wizard springt damit weiter. */
  onGewaehlt?: () => void;
  /** Feldname nur für Screenreader, wenn ihn schon der Schritttitel nennt (nur Karten, Knöpfe, Tabelle). */
  beschriftungVerbergen?: boolean;
};

/**
 * Rendert ein einzelnes Feld samt Zustand – rein aus der Antwort, ohne
 * Produktwissen. Bietet der Server am Feld Aktionen an, stehen ihre Knöpfe
 * darunter, gleich in welcher Darstellung.
 */
export function FeldEingabe(props: FeldEingabeProps) {
  const { aktionen } = props.feld;
  // Immer derselbe Aufbau: Kommt eine Aktion hinzu oder fällt weg, behält das Feld Fokus und Zustand.
  return (
    <>
      <FeldDarstellung {...props} />
      {aktionen?.length ? <FeldAktionen feld={props.feld.schluessel} aktionen={aktionen} /> : null}
    </>
  );
}

/**
 * Das Eingabeelement eines Felds. Gesperrte Felder sind berechnet: sichtbar,
 * nicht änderbar. Wünscht der Server eine Darstellung (Karten, Knöpfe, Tabelle),
 * gilt sie; sonst das schlichte Eingabefeld.
 */
function FeldDarstellung({ feld, wert, onChange, onGewaehlt, beschriftungVerbergen }: FeldEingabeProps) {
  const { gesperrt } = feld.zustand;
  const unten = <FeldFuss feld={feld} />;
  const auswahl = typeof wert === "string" ? wert : "";

  if (feld.art === "auswahl" && feld.darstellung === "karten") {
    return (
      <AuswahlKarten
        feld={feld}
        wert={auswahl}
        onChange={onChange}
        onGewaehlt={onGewaehlt}
        beschriftungVerbergen={beschriftungVerbergen}
      />
    );
  }
  if (feld.art === "auswahl" && feld.darstellung === "knoepfe") {
    return <Knopfreihe feld={feld} wert={wert} onChange={onChange} beschriftungVerbergen={beschriftungVerbergen} />;
  }
  if (feld.art === "auswahl" && feld.darstellung === "tabelle" && feld.tabelle) {
    return (
      <AuswahlTabelle
        feld={{ ...feld, tabelle: feld.tabelle }}
        wert={auswahl}
        onChange={onChange}
        beschriftungVerbergen={beschriftungVerbergen}
      />
    );
  }
  if (feld.art === "mass" && feld.darstellung === "knoepfe" && (feld.max - feld.min) / feld.schritt + 1 <= MAX_KNOPF_STUFEN) {
    return <Knopfreihe feld={feld} wert={wert} onChange={onChange} beschriftungVerbergen={beschriftungVerbergen} />;
  }

  if (feld.art === "mass") {
    return (
      <label className="field">
        <Beschriftung feld={feld} zusatz={bereich(feld)} />
        {/* Geleert heißt „kein Wert“ (""), nicht 0 – erfal berechnet manche Mengen selbst. */}
        <input
          type="number"
          min={feld.min}
          max={feld.max}
          step={feld.schritt}
          disabled={gesperrt}
          value={typeof wert === "number" ? wert : ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
        {unten}
      </label>
    );
  }

  if (feld.art === "schalter") {
    return (
      <div className="field">
        <label className="field field--inline">
          <input type="checkbox" checked={wert === true} disabled={gesperrt} onChange={(e) => onChange(e.target.checked)} />
          <Beschriftung feld={feld} zusatz={aufpreis(feld)} />
        </label>
        {unten}
      </div>
    );
  }

  if (feld.art === "text") {
    return (
      <label className="field">
        <Beschriftung feld={feld} />
        <input type="text" disabled={gesperrt} value={typeof wert === "string" ? wert : ""} onChange={(e) => onChange(e.target.value)} />
        {unten}
      </label>
    );
  }

  // Mit Optionsbildern (z. B. JAB-Farben) eine Bildauswahl. Bewusst kein <label>: Ein Klick auf
  // Beschriftung oder Hinweis öffnete sonst die Liste; den Namen bekommt der Knopf über `label`.
  if (feld.optionen.some((option) => option.bildUrl)) {
    return (
      <div className="field">
        <Beschriftung feld={feld} />
        <BildAuswahl
          label={feld.label}
          optionen={feld.optionen.map((option) => ({
            wert: option.wert,
            label: option.label,
            bildUrl: option.bildUrl,
            zusatz: option.aufpreisCents ? `+${euro(option.aufpreisCents)}` : null,
            gesperrt: option.gesperrt,
          }))}
          wert={auswahl}
          onChange={onChange}
          disabled={gesperrt}
          leerOption={feld.zustand.pflicht ? null : "– keine Auswahl –"}
          platzhalter="– bitte wählen –"
        />
        {unten}
      </div>
    );
  }

  return (
    <label className="field">
      <Beschriftung feld={feld} />
      <select value={auswahl} disabled={gesperrt} onChange={(e) => onChange(e.target.value)}>
        {!feld.zustand.pflicht && <option value="">– keine Auswahl –</option>}
        {/* Pflichtfeld ohne Wert: Ohne Platzhalter zeigte der Browser die erste
            Option als gewählt, obwohl nichts gewählt ist und nichts gesendet wird. */}
        {feld.zustand.pflicht && auswahl === "" && (
          <option value="" disabled>
            – bitte wählen –
          </option>
        )}
        {feld.optionen.map((option) => (
          <option key={option.wert} value={option.wert}>
            {option.label}
            {option.aufpreisCents ? ` (+${euro(option.aufpreisCents)})` : ""}
          </option>
        ))}
      </select>
      {unten}
    </label>
  );
}
